import React, { useEffect, useState, useRef } from "react";
import "../../assets/styles/local/QuoteCalculation.css";
import Header from "../../layouts/Header";
import { useHistory } from "react-router-dom";
import ExpandIcon from "../../assets/icons/Expand icon.svg";
import NotShow from "../../assets/icons/Not show.svg";
import Package from "../../assets/icons/package.svg";
import Plus from "../../assets/icons/Plus.svg";
import Kebab from "../../assets/icons/Kebab Calculations.svg";
import whiteKebab from "../../assets/icons/whiteKebab.svg";
import delete1 from "../../assets/icons/Group 2723.svg";
import edit from "../../assets/icons/edit.svg";
import duplicate from "../../assets/icons/Duplicate.svg";
import arrow from "../../assets/icons/down arrow.svg";
import downArrowWhite from "../../assets/icons/downArrowWhite.svg";
import Show from "../../assets/icons/eye.svg";
import Bin from "../../assets/icons/Bin - Copy.svg";
import cross from "../../assets/icons/x-circle-3.svg";
import cross2 from "../../assets/icons/Xicon.svg";
import {
  ProductDropdown,
  AddSection,
  getQuoteSections,
  DuplicateSection,
  DeleteSection,
  updateQuoteSections,
  deleteUpdateQuoteState,
  addSubSection,
  deleteSubSection,
  sendApprovedQuotesEmail,
  DeleteSubSectionParts,
} from "../../redux/actions/action";
import { useDispatch, useSelector } from "react-redux";
import { europeanFormatWithSymbol } from "../../utils/European-Format/NumberFormatter";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { stateToHTML } from "draft-js-export-html";
import QuotesApprovedPopup from "../../components/Dailogs/QuotesApprovedPopup";
import QuotesApprovedMailPopup from "../../components/Dailogs/QuotesApprovedMailPopup";
import { ROLES } from "../../utils/helpers/userRole";
import { handleGeneralNotification } from "../../utils/helpers/notifications";

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const calculateSectionTotals = (subSections = []) =>
  (subSections || []).reduce(
    (totals, subSection) => {
      if (subSection?.subSectionParts?.length) {
        subSection.subSectionParts.forEach((part) => {
          totals.amount += toNumber(part?.total);
          totals.hours += toNumber(part?.hours);
        });
      } else {
        totals.amount += toNumber(subSection?.total);
        totals.hours += toNumber(subSection?.hours);
      }

      return totals;
    },
    { amount: 0, hours: 0 },
  );

const getSectionAmount = (section) => {
  const calculatedTotal = calculateSectionTotals(section?.subSections).amount;
  return calculatedTotal || toNumber(section?.totalAmount);
};

const formatDescription = (description) => {
  if (description?.getCurrentContent) {
    return stateToHTML(description.getCurrentContent());
  }

  return description ?? "";
};

const getSubSectionPartsTotal = (subSection) =>
  (subSection?.subSectionParts || []).reduce(
    (sum, part) => sum + toNumber(part?.total),
    0,
  );

const getPayloadSectionTotalAmount = (subSections = []) =>
  (subSections || []).reduce(
    (sum, subSection) => sum + toNumber(subSection?.total),
    0,
  );

const buildAppendixPart = (part, indexPosition) => {
  const { subsectionPartid, subsectionid, ...partPayload } = part;
  return {
    ...partPayload,
    indexPosition,
  };
};

const getAppendixPartSignature = (part) =>
  `${part?.productPartid || ""}:${part?.qty || ""}:${part?.unitPrice || ""}:${
    part?.total || ""
  }`;

const getExtraWorkAppendixSubSection = (appendixSection) => {
  let extraWorkSubSection = appendixSection.subSections.find(
    (subSection) => subSection?.title === "Extra Work",
  );

  if (!extraWorkSubSection) {
    extraWorkSubSection = {
      title: "Extra Work",
      description: "",
      subSectionParts: [],
      indexPosition: appendixSection.subSections.length + 1,
      total: 0,
      totalizeAmountInSectionHeader: 1,
    };
    appendixSection.subSections.push(extraWorkSubSection);
  }

  return extraWorkSubSection;
};

const buildQuoteSectionsPayload = (sections = []) => {
  const sectionEntries = (sections || []).map((section) => ({
    source: section,
    payload: {
      ...section,
      subSections: [],
    },
  }));
  const list = sectionEntries.map((entry) => entry.payload);

  const getTargetSection = (sectionName) => {
    let targetSection = list.find(
      (section) => section?.sectionName === sectionName,
    );

    if (!targetSection) {
      targetSection = {
        sectionName,
        subSections: [],
      };
      list.push(targetSection);
    }

    return targetSection;
  };

  let appendixSection = list.find(
    (section) => section?.sectionName === "Appendix",
  );

  const getAppendixSection = () => {
    if (!appendixSection) {
      appendixSection = getTargetSection("Appendix");
    }

    return appendixSection;
  };

  sectionEntries.forEach(({ source, payload }) => {
    (source?.subSections || []).forEach((subSection) => {
      const targetSection =
        subSection?.sectionName &&
        subSection?.sectionName !== source?.sectionName
          ? getTargetSection(subSection.sectionName)
          : payload;
      const sub = {
        ...subSection,
        description: formatDescription(subSection?.description),
      };

      delete sub["sectionName"];
      targetSection.subSections.push(sub);

      if (
        source?.sectionName === "EW - Extra Work" &&
        subSection?.subSectionParts?.length
      ) {
        const targetAppendixSection = getAppendixSection();
        const extraWorkSubSection = getExtraWorkAppendixSubSection(
          targetAppendixSection,
        );

        subSection.subSectionParts.forEach((part) => {
          const signature = getAppendixPartSignature(part);
          const alreadyInAppendix = extraWorkSubSection.subSectionParts.some(
            (appendixPart) =>
              getAppendixPartSignature(appendixPart) === signature,
          );

          if (!alreadyInAppendix) {
            extraWorkSubSection.subSectionParts.push(
              buildAppendixPart(
                part,
                extraWorkSubSection.subSectionParts.length + 1,
              ),
            );
          }
        });

        extraWorkSubSection.total =
          getSubSectionPartsTotal(extraWorkSubSection);
      }
    });
  });

  return list.map((section) => ({
    ...section,
    totalAmount: getPayloadSectionTotalAmount(section?.subSections),
  }));
};

const QuoteCalculation = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const popupRef = useRef(null);
  const heightRef = useRef(null);
  const [allSectionsHeight, setAllSectionsHeight] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [showSections, setShowSections] = useState(false);
  const [productList, setProductList] = useState([]);
  const [fullProductList, setFullProductList] = useState([]);
  const [sectionName, setSectionName] = useState("");
  const [quoteId, setQuoteId] = useState("");
  const [quoteCode, setQuoteCode] = useState("");
  const [margin, setMargin] = useState("");
  const [uLaborCost, setULaborCost] = useState("");
  const [sectionList, setSectionList] = useState([]);
  const [openSectionIndex, setOpenSectionIndex] = useState(null);
  const [total, setTotal] = useState("0");
  const [pageFunction, setPageFunction] = useState("");
  const [backLocation, setBackLocation] = useState("");
  const [showAllSections, setShowAllSections] = useState(false);
  const [expandedSectionIndices, setExpandedSectionIndices] = useState([]);
  const [createdBy, setCreatedBy] = useState("");
  const [isApproved, setIsApproved] = useState(false);

  const roleAccess = window.localStorage.getItem("roleid");

  const fetchProductDropdown = useSelector(
    (state) => state.user.fetchProductDropdown,
  );
  const addSectionResponse = useSelector(
    (state) => state.user.addSectionResponse,
  );
  const getQuoteSectionsResponse = useSelector(
    (state) => state.user.getQuoteSectionsResponse,
  );
  const deleteSectionResponse = useSelector(
    (state) => state.user.deleteSectionResponse,
  );
  const duplicateSectionResponse = useSelector(
    (state) => state.user.duplicateSectionResponse,
  );
  const addSubSectionResponse = useSelector(
    (state) => state.user.addSubSectionResponse,
  );
  const deleteSubSectionResponse = useSelector(
    (state) => state.user.deleteSubSectionResponse,
  );
  const deleteSubSectionPartsRes = useSelector(
    (state) => state.user.deleteSubSectionPartsRes,
  );
  const updateQuoteSectionsResponse = useSelector(
    (state) => state.user.updateQuoteSectionsResponse,
  );

  const sendApprovedQuotesEmailRes = useSelector(
    (state) => state.user.sendApprovedQuotesEmailRes,
  );

  const onDragEnd = (result) => {
    if (result?.destination !== null) {
      let list = [...sectionList];
      let source = list.slice(
        result?.source?.index,
        result?.source?.index + 1,
      )[0];
      list.splice(result?.source?.index, 1);
      list.splice(result?.destination?.index, 0, source);
      list.map((section, index) => {
        section["indexPosition"] = index;
      });
      setSectionList([...list]);
      if (openSectionIndex === null) return;
      if (openSectionIndex === result?.source?.index)
        return setOpenSectionIndex(result?.destination?.index);
      if (
        openSectionIndex !== result?.source?.index &&
        openSectionIndex === result?.destination?.index
      )
        return setOpenSectionIndex(result?.source?.index);
      if (openSectionIndex !== result?.destination?.index) return;
      setOpenSectionIndex(result?.destination?.index);
    }
  };

  useEffect(() => {
    setAllSectionsHeight(heightRef.current.clientHeight);
  });

  useEffect(() => {
    dispatch(ProductDropdown());
    setQuoteCode(history.location?.state?.split("@@")[0]);
    setQuoteId(history.location?.state?.split("@@")[1]);
    setPageFunction(history.location?.state?.split("@@")[2]);
    setBackLocation(history.location?.state?.split("@@")[3]);
    dispatch(getQuoteSections(history?.location?.state?.split("@@")[1]));
  }, [addSubSectionResponse, deleteSubSectionPartsRes]);

  useEffect(() => {
    if (fetchProductDropdown?.status === true) {
      setProductList(fetchProductDropdown?.data);
      setFullProductList(fetchProductDropdown?.data);
    }
  }, [fetchProductDropdown]);

  useEffect(() => {
    if (addSectionResponse?.status === true) {
      setShowPopup(false);
      setSectionName("");
      setSectionList((list) => [...list, addSectionResponse?.data]);
    }
  }, [addSectionResponse]);

  useEffect(() => {
    if (addSubSectionResponse?.status === true) {
      const sectionid = addSubSectionResponse?.data[0]?.sectionid;
      let list = [...sectionList];
      let index = list.findIndex((section) => section.sectionid === sectionid);
      addSubSectionResponse?.data?.map((item, i) => {
        list[index]?.subSections.push(item);
      });
      setSectionList([...list]);
    }
  }, [addSubSectionResponse]);

  useEffect(() => {
    if (
      deleteSubSectionResponse?.data?.status === true &&
      sectionList?.length > 0
    ) {
      let list = [...sectionList];
      const sectionIndex = list?.findIndex(
        (section) => section?.sectionid === deleteSubSectionResponse?.sectionid,
      );
      const singleSubSection = list[sectionIndex]?.subSections;
      const subSectionIndex = singleSubSection?.findIndex(
        (subSection) =>
          subSection?.subsectionid == deleteSubSectionResponse?.subsectionid,
      );
      list[sectionIndex]?.subSections.splice(subSectionIndex, 1);
      let totalAmount = 0;
      list[sectionIndex]?.subSections?.map(
        (subSection) => (totalAmount += +subSection?.total),
      );
      list[sectionIndex]["totalAmount"] = totalAmount;
      let vatTotal2 = 0;
      list?.map((section) => (vatTotal2 += +section.totalAmount));
      setTotal(vatTotal2);
      setSectionList([...list]);
    }
  }, [deleteSubSectionResponse]);

  useEffect(() => {
    if (deleteSectionResponse?.status === true) {
      dispatch(getQuoteSections(quoteId));
      setTotal(deleteSectionResponse?.data);
    }
  }, [deleteSectionResponse]);

  useEffect(() => {
    if (duplicateSectionResponse?.status === true) {
      setSectionList((list) => [...list, duplicateSectionResponse?.data]);
      setTotal(duplicateSectionResponse?.data?.totalAmount + +total);
    }
  }, [duplicateSectionResponse]);

  useEffect(() => {
    if (updateQuoteSectionsResponse?.status === true) {
      if (isApproved === "Approved") {
        setApproveQuotesEmailPopup(true);
      } else {
        history.goBack();
      }
      // history.goBack();
      dispatch(deleteUpdateQuoteState());
    }
  }, [updateQuoteSectionsResponse]);

  useEffect(() => {
    if (getQuoteSectionsResponse?.status === true) {
      setSectionList(getQuoteSectionsResponse?.data?.sections);
      setTotal(getQuoteSectionsResponse?.data?.total);
      setMargin(getQuoteSectionsResponse?.data?.margin ?? "");
      setULaborCost(getQuoteSectionsResponse?.data?.unitlabelcost ?? "");
      setIsApproved(getQuoteSectionsResponse?.data?.status);
    }
  }, [getQuoteSectionsResponse]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowPopup(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popupRef]);

  let financialTab = history.location?.state?.split("@@")[3];
  let sectionNameFilter = sectionList.find(
    (item) => item.sectionName === "EW - Extra Work",
  );

  useEffect(() => {
    if (
      sectionNameFilter?.sectionName === "EW - Extra Work" &&
      financialTab === "/financial"
    ) {
      setOpenSectionIndex(sectionNameFilter?.indexPosition);
    }
  }, [sectionNameFilter]);

  // asc addSection responce start
  // let quoteId2 = history.location?.state?.split("@@")[1];

  // let financialTab = history.location?.state?.split("@@")[3];

  // useEffect(() => {
  //   if (getQuoteSectionsResponse?.status === true) {
  //     if (
  //       financialTab === "/financial" &&
  //       deleteSectionResponse.status !== true
  //     ) {
  //       dispatch(
  //         AddSection(
  //           quoteId2,
  //           "EW - Extra Work",
  //           getQuoteSectionsResponse?.data?.sections?.length
  //         )
  //       );
  //     }
  //   }
  // }, [getQuoteSectionsResponse]);
  // asc addSection responce end

  const [approvedConfirmationPopup, setApprovedConfirmationPopup] =
    useState(false);
  const [approveQuotesEmailPopup, setApproveQuotesEmailPopup] = useState(false);
  const quoteSectionsPayload = buildQuoteSectionsPayload(sectionList);
  const quoteTotal = sectionList.reduce(
    (sum, section) => sum + getSectionAmount(section),
    0,
  );
  const quoteData = getQuoteSectionsResponse?.data || {};
  const updateQuotePayload = {
    maincontact: quoteData?.maincontact,
    priorityid: quoteData?.priorityid,
    projectcode: quoteData?.projectcode,
    projectid: quoteData?.projectid,
    sections: quoteSectionsPayload,
    quoteid: quoteId,
    reference: quoteData?.reference,
    quoteDate: quoteData?.quoteDate,
    quotecode: quoteCode,
    total:
      quoteSectionsPayload.length > 0 ? quoteSectionsPayload[0].totalAmount : 0,
    status: "Draft",
  };

  const handleUpdateQuotes = (e) => {
    // history.push(
    //   "/quote-calculation",
    //   `${quoteCode}@@${quoteid}@@Change@@/quote`
    // );
    dispatch(updateQuoteSections(quoteId, updateQuotePayload));
    setApprovedConfirmationPopup(false);
  };

  const [emailFromQuotesMailPopup, setEmailFromQuotesMailPopup] = useState("");

  const handleSendEmailQuotes = (e) => {
    dispatch(sendApprovedQuotesEmail(quoteId, emailFromQuotesMailPopup));
    history.goBack();
    setApproveQuotesEmailPopup(false);
  };

  return (
    <>
      <Header
        leftComponent={
          <div
            className="flex-wrap d-flex align-items-baseline"
            style={{ gap: "18px" }}
          >
            <div
              className="projectText"
              onClick={() => {
                history.push("/quote");
                dispatch(deleteUpdateQuoteState());
              }}
            >
              Quotes
            </div>
            <div className="quote-cal-head-white">
              <p className="">Quote</p>
              {/* <p className="pt-3 projectID ps-5"> ID &nbsp; ID 23.1064</p> */}
              <p className=""> ID {quoteCode}</p>
            </div>

            {isApproved === "Approved" && (
              <div className="statusApprovedSection">
                <p className="">Status:</p>
                {/* <p className="pt-3 projectID ps-5"> ID &nbsp; ID 23.1064</p> */}
                <p className="fw-bold"> Approved</p>
              </div>
            )}
          </div>
        }
      />

      {approvedConfirmationPopup && (
        <QuotesApprovedPopup
          animation={true}
          // handleDelete={handleDelete}
          handleDelete={handleUpdateQuotes}
          show={approvedConfirmationPopup}
          // onHide={() => setApprovedConfirmationPopup(false)}
          onHide={() => history.goBack()}
        />
      )}

      {approveQuotesEmailPopup && (
        <QuotesApprovedMailPopup
          animation={true}
          handleDelete={handleSendEmailQuotes}
          show={approveQuotesEmailPopup}
          // onHide={() => setApproveQuotesEmailPopup(false)}
          onHide={() => history.goBack()}
          setQuotesEmail={setEmailFromQuotesMailPopup}
        />
      )}

      <div className="quote-cal-main-container">
        <div className="quote-cal-main-cont">
          <div
            onClick={() => {
              setShowAllSections(!showAllSections);
              if (!showAllSections && expandedSectionIndices.length === 0) {
                const allIndices = sectionList.map((_, index) => index);
                setExpandedSectionIndices(allIndices);
              } else {
                setExpandedSectionIndices([]);
              }
            }}
          >
            <img
              className="hide-on-mobile"
              src={ExpandIcon}
              alt="Expand Icon"
            />
            <p className="hide-on-mobile">Expand</p>
          </div>
          <div className="all-sections-div">
            <section className="all-sections-1">
              <article className="all-sections-eye">
                <img
                  src={showSections ? NotShow : Show}
                  onClick={() => setShowSections(!showSections)}
                />
                <p>Sections</p>
              </article>
              <article className="all-sections-eye-2">
                <img src={Plus} onClick={() => setShowPopup(true)} />
                <p onClick={() => setShowPopup(true)}>Add New</p>
                <article
                  className="add-sec-popup"
                  ref={popupRef}
                  style={{
                    transform: showPopup ? "scale(1)" : "scale(0)",
                    transitionDuration: "300ms",
                  }}
                >
                  <p className="new-section-text">New Section</p>
                  <article className="add-sec-article">
                    <input
                      className="add-sec-input"
                      value={sectionName}
                      onChange={(e) => setSectionName(e.target.value)}
                    />
                    <article className="add-sec-under"></article>
                    <p className="add-sec-name">Name</p>
                  </article>
                  <article className="add-sec-buttons">
                    <button
                      className="cancel-btn"
                      onClick={() => {
                        setSectionName("");
                        setShowPopup(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="add-btn"
                      onClick={() => {
                        if (roleAccess === ROLES.VIEWER)
                          return handleGeneralNotification({
                            notificationType: "CUSTOM",
                            text: `You don't have permission to add/edit quotes.`,
                          });
                        dispatch(
                          AddSection(quoteId, sectionName, sectionList?.length),
                        );
                      }}
                    >
                      Add
                    </button>
                  </article>
                </article>
              </article>
            </section>
            <div
              style={{
                height: `${allSectionsHeight}px`,
                transitionDuration: "300ms",
              }}
            >
              <div
                className="all-sections-grid"
                ref={heightRef}
                style={{ display: showSections ? "none" : "grid" }}
              >
                {sectionList?.map((section, index) => {
                  return (
                    <p
                      className="all-sections-name ellipse"
                      key={`${index}-${section?.sectionid}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        if (openSectionIndex === index) {
                          return setOpenSectionIndex(null);
                          setOpenSectionIndex(index);
                        } else if (expandedSectionIndices.includes(index)) {
                          setExpandedSectionIndices((prevIndices) =>
                            prevIndices.filter((i) => i !== index),
                          );
                        } else {
                          setExpandedSectionIndices((prevIndices) => [
                            ...prevIndices,
                            index,
                          ]);
                        }
                      }}
                    >
                      <span style={{ fontWeight: "bold" }}>
                        {(+index + 1)?.toString().padStart(2, "0")}
                      </span>{" "}
                      - {section?.sectionName}
                    </p>
                  );
                })}
              </div>
            </div>
          </div>
          <DragDropContext onDragEnd={(result) => onDragEnd(result)}>
            <Droppable droppableId={123456} key={`droppable-${123456}`}>
              {(provided, snapshot) => {
                return (
                  <div
                    key={`droppable-div-${123456}`}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{ minHeight: `${5.5 * sectionList?.length}rem` }}
                  >
                    {sectionList?.map((section, index) => {
                      return (
                        <Draggable
                          key={`draggable-${section?.sectionid}`}
                          draggableId={section?.sectionid}
                          index={index}
                        >
                          {(provided, snapshot) => {
                            return (
                              <SingleSection
                                provided={provided}
                                key={`${index}-${section?.sectionid}`}
                                productList={productList}
                                fullProductList={fullProductList}
                                setProductList={setProductList}
                                sectionid={section?.sectionid}
                                sectionName={section?.sectionName}
                                comment={section?.comment}
                                indexPosition={section?.indexPosition}
                                totalAmount={section?.totalAmount}
                                totalHours={section?.totalHours}
                                subSections={section?.subSections}
                                index={index}
                                openSectionIndex={openSectionIndex}
                                setOpenSectionIndex={setOpenSectionIndex}
                                sectionList={sectionList}
                                setSectionList={setSectionList}
                                vatTotal={total}
                                setVatTotal={setTotal}
                                showAllSections={showAllSections}
                                setShowAllSections={setShowAllSections}
                                roleAccess={roleAccess}
                                quoteid={quoteId}
                                margin={margin}
                                uLaborCost={uLaborCost}
                                expandedSectionIndices={expandedSectionIndices}
                                setExpandedSectionIndices={
                                  setExpandedSectionIndices
                                }
                                isApproved={isApproved}
                              />
                            );
                          }}
                        </Draggable>
                      );
                    })}
                  </div>
                );
              }}
            </Droppable>
          </DragDropContext>
          <div className="total-exc-vat">
            <p></p>
            <p style={{ margin: "-2em" }}>Total Excl. Vat</p>
            <p></p>
            <p style={{ textAlign: "end" }}>
              {europeanFormatWithSymbol(quoteTotal)}
            </p>
            <p></p>
          </div>
          <div className="cal-page-btns pb-4">
            <button
              className="cal-page-cancel"
              onClick={() => {
                history.goBack();
                dispatch(deleteUpdateQuoteState());
              }}
            >
              Cancel
            </button>
            {roleAccess !== ROLES.VIEWER && (
              <button
                className="cal-page-cancel cal-page-print"
                onClick={() => {
                  history.push(`/quote/${quoteId}/pdf`);
                }}
              >
                Print
              </button>
            )}
            {roleAccess !== ROLES.VIEWER && (
              <button
                className="cal-page-change"
                onClick={() => {
                  if (roleAccess === ROLES.VIEWER)
                    return handleGeneralNotification({
                      notificationType: "CUSTOM",
                      text: `You don't have permission to add/edit quotes.`,
                    });

                  if (isApproved === "Approved") {
                    setApprovedConfirmationPopup(true);
                  } else {
                    dispatch(updateQuoteSections(quoteId, updateQuotePayload));
                  }

                  // dispatch(
                  //   updateQuoteSections(quoteId, {
                  //     quoteid: quoteId,
                  //     quotecode: quoteCode,
                  //     total: total,
                  //     sections: sectionList,
                  //   })
                  // );
                }}
              >
                {pageFunction}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default QuoteCalculation;

const SingleSection = ({
  provided,
  key,
  productList,
  fullProductList,
  setProductList,
  sectionid,
  sectionName,
  comment,
  indexPosition,
  totalAmount,
  totalHours,
  subSections,
  index,
  openSectionIndex,
  setOpenSectionIndex,
  sectionList,
  setSectionList,
  vatTotal,
  setVatTotal,
  showAllSections,
  roleAccess,
  quoteid,
  margin,
  uLaborCost,
  setShowAllSections,
  expandedSectionIndices,
  setExpandedSectionIndices,
  isApproved,
}) => {
  const dispatch = useDispatch();
  const [showMenu, setShowMenu] = useState(false);
  const [showMaterialPopup, setShowMaterialPopup] = useState(false);
  const sectionHeightRef = useRef(null);
  const materialPopupRef = useRef(null);
  const [sectionHeight, setSectionHeight] = useState(0);
  const [material, setMaterial] = useState("");
  const [commentState, setCommentState] = useState(comment);
  const [showComment, setShowComment] = useState(false);
  const calculatedSectionTotals = calculateSectionTotals(subSections);
  const totalAmountFromProp =
    totalAmount !== undefined && totalAmount !== null
      ? toNumber(totalAmount)
      : 0;
  const totalHoursFromProp =
    totalHours !== undefined && totalHours !== null ? toNumber(totalHours) : 0;
  const totalAmountValue =
    calculatedSectionTotals.amount || totalAmountFromProp;
  const totalHoursValue = calculatedSectionTotals.hours || totalHoursFromProp;
  const [totalHoursMain, setTotalHoursMain] = useState(totalHoursValue);
  const [totalAmountMain, setTotalAmountMain] = useState(totalAmountValue);
  const [singleSectionName, setSingleSectionName] = useState(sectionName);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const packageId = window.localStorage.getItem("packageId");

  const materialInputRef = useRef(null);

  useEffect(() => {
    if (showMaterialPopup) {
      materialInputRef.current.focus();
    }
  }, [showMaterialPopup]); // riya code for higlight for cursor.

  const onDragEndSubSection = (result) => {
    if (result?.destination !== null) {
      let list = [...sectionList];
      let source = list[index]?.subSections.slice(
        result?.source?.index,
        result?.source?.index + 1,
      )[0];
      list[index]?.subSections.splice(result?.source?.index, 1);
      list[index]?.subSections.splice(result?.destination?.index, 0, source);
      list[index]?.subSections.map((section, index) => {
        section["indexPosition"] = index;
      });
      setSectionList([...list]);
    }
  };

  useEffect(() => {
    setSectionHeight(sectionHeightRef.current.clientHeight);
  });

  useEffect(() => {
    setTotalAmountMain(totalAmountValue);
    setTotalHoursMain(totalHoursValue);
  }, [totalAmountValue, totalHoursValue]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        materialPopupRef.current &&
        !materialPopupRef.current.contains(event.target)
      ) {
        setShowMaterialPopup(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [materialPopupRef]);

  useEffect(() => {
    if (material) {
      let list = [...fullProductList];
      list = list?.filter((product) => {
        return product?.productPartName
          ?.toLowerCase()
          .startsWith(material.toLowerCase());
      });
      setProductList(list);
    } else {
      setProductList([...fullProductList]);
    }
  }, [material]);

  // useEffect(() => {
  //   if (subSections?.length > 0) {
  //     setSelectedProducts([...subSections]);
  //   }
  // }, []);

  /// ASC CLOSE DROP DOWN CLICK OUT SIDE START ///
  const kebabRef = useRef();
  const addCommentBoxRef = useRef();

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, false);
    return () => {
      document.removeEventListener("click", handleClickOutside, false);
    };
  }, []);

  const handleClickOutside = (event) => {
    if (kebabRef.current && !kebabRef.current.contains(event.target)) {
      setShowMenu(false);
    }

    if (
      addCommentBoxRef.current &&
      !addCommentBoxRef.current.contains(event.target)
    ) {
      // setShowComment(false);
    }
  };
  /// ASC CLOSE DROP DOWN CLICK OUT SIDE END ///

  const productPartClickRef = useRef([]);

  return (
    <div
      className="single-section-div"
      key={key}
      ref={provided.innerRef}
      {...provided.draggableProps}
    >
      <div
        className={
          isApproved === "Approved"
            ? "single-section-div-head-Approved"
            : "single-section-div-head"
        }
        {...provided.dragHandleProps}
      >
        <div style={{ position: "relative" }} ref={kebabRef}>
          <img
            src={isApproved === "Approved" ? whiteKebab : Kebab}
            className="pointer"
            width={isApproved === "Approved" && 40}
            alt=""
            onClick={() => setShowMenu(true)}
          />
          <div
            className="kebab-menu"
            style={{
              transform: showMenu ? "scale(1)" : "scale(0)",
              transitionDuration: "300ms",
            }}
          >
            <img
              alt=""
              src={Kebab}
              onClick={() => setShowMenu(false)}
              style={{ cursor: "pointer" }}
            />
            <div className="kebab-option-div">
              <section
                onClick={() => {
                  if (roleAccess === ROLES.VIEWER)
                    return handleGeneralNotification({
                      notificationType: "CUSTOM",
                      text: `You don't have permission to add/edit quotes.`,
                    });
                  dispatch(DuplicateSection(sectionid));
                  setShowMenu(false);
                }}
              >
                <img src={duplicate} alt="" />
                <p>Duplicate</p>
              </section>
              <section
                onClick={(e) => {
                  e.stopPropagation(); //like => e.preventDefault.

                  if (roleAccess === ROLES.VIEWER)
                    return handleGeneralNotification({
                      notificationType: "CUSTOM",
                      text: `You don't have permission to add/edit quotes.`,
                    });
                  setShowComment(true);
                  setShowMenu(false);
                }}
              >
                <img src={edit} alt="" />
                <p>Add Comment</p>
              </section>
              <section
                onClick={() => {
                  if (roleAccess === ROLES.VIEWER)
                    return handleGeneralNotification({
                      notificationType: "CUSTOM",
                      text: `You don't have permission to add/edit quotes.`,
                    });
                  dispatch(DeleteSection(quoteid, sectionid));
                  setShowMenu(false);
                }}
              >
                <img src={delete1} alt="" />
                <p>Delete</p>
              </section>
            </div>
          </div>
          <div
            ref={addCommentBoxRef}
            className="comment-popup"
            style={{
              transform: showComment ? "scale(1)" : "scale(0)",
              transitionDuration: "300ms",
            }}
          >
            <div className="add-comment-flex">
              <p>Section Comment</p>
              <img
                src={cross}
                alt=""
                style={{ cursor: "pointer" }}
                onClick={() => setShowComment(false)}
              />
            </div>
            <textarea
              placeholder="Add a comment"
              className="comment-textarea"
              value={commentState}
              onChange={(e) => {
                setCommentState(e.target.value);
                let list = [...sectionList];
                list = list[index]["comment"] = e.target.value;
                setSectionList([...sectionList]);
              }}
            />
          </div>
        </div>
        <div className="d-flex align-items-start">
          <p
            className="total-h-text"
            style={{ fontWeight: "bold", width: "30px" }}
          >
            {(+index + 1)?.toString().padStart(2, "0")} -
          </p>
          <textarea
            className="section-name-input"
            readOnly
            value={singleSectionName}
            onChange={(e) => {
              setSingleSectionName(e.target.value);
              let list = [...sectionList];
              list = list[index]["sectionName"] = e.target.value;
              setSectionList([...sectionList]);
            }}
            title={singleSectionName.length > 10 && singleSectionName}
            style={{
              whiteSpace: "pre-wrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: isApproved === "Approved" && "white",
            }}
          />
        </div>
        <div style={{ textAlign: "end" }}>
          <p className="total-h-text hide-on-mobile">Total Hours</p>
          <p
            className="total-h-text hide-on-mobile"
            style={{ fontWeight: "bold" }}
          >
            {totalHoursMain}
          </p>
        </div>
        <div className="total-amounrt-mobile" style={{ textAlign: "end" }}>
          <p className="total-h-text totalamountmobile">Total Amount</p>
          <p
            className="total-h-text zeroshiftleft"
            style={{ fontWeight: "bold" }}
          >
            {europeanFormatWithSymbol(totalAmountMain)}
          </p>
        </div>
        <div
          className="image-in-mobileshow"
          style={{ textAlign: "end", cursor: "pointer" }}
        >
          <img
            src={isApproved === "Approved" ? downArrowWhite : arrow}
            alt=""
            onClick={() => {
              if (expandedSectionIndices.includes(index)) {
                setExpandedSectionIndices((prevIndices) =>
                  prevIndices.filter((i) => i !== index),
                );
              } else {
                setExpandedSectionIndices((prevIndices) => [
                  ...prevIndices,
                  index,
                ]);
              }
              // if (openSectionIndex === index) return setOpenSectionIndex(null);
              // setOpenSectionIndex(index);
            }}
            style={{
              transform: expandedSectionIndices.includes(index)
                ? "rotate(180deg)"
                : "rotate(0deg)",
              transition: "transform 300ms",
            }}
          />
        </div>
      </div>

      <div
        className={sectionName === "General" && "pe-none"}
        style={{ transitionDuration: "500ms" }}
      >
        {/* <div style={{ height: `${sectionHeight}px`, transitionDuration: "500ms" }}> */}
        <div
          ref={sectionHeightRef}
          style={{
            display: expandedSectionIndices.includes(index) ? "block" : "none",
          }}
        >
          <div className="single-sec-cont-head" style={{ cursor: "default" }}>
            <div></div>
            <p>Material</p>
            <p style={{ textAlign: "center" }}>Qty</p>
            <p style={{ textAlign: "end" }}>U. Price</p>
            <p style={{ textAlign: "end", color: "#323232" }}>
              Material <br />
              Total
            </p>
            <p style={{ textAlign: "end" }}>Hours</p>
            <p style={{ textAlign: "end" }}>U. Price</p>
            <p style={{ textAlign: "end", color: "#323232" }}>Hours Total</p>
            <p style={{ textAlign: "center" }}>Margin</p>
            <p style={{ textAlign: "end", color: "#323232" }}>Total</p>
            <p style={{ textAlign: "end" }}>Provisional</p>
          </div>
          <DragDropContext onDragEnd={(result) => onDragEndSubSection(result)}>
            <Droppable droppableId={654321} key={`droppable-${654321}`}>
              {(provided, snapshot) => {
                return (
                  <div
                    key={`droppable-div-${654321}`}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    // style={{ minHeight: `${7.1 * subSections?.length}rem` }}
                    style={{ minHeight: `${6.1 * subSections?.length}rem` }}
                  >
                    {subSections?.map((item, subSectionIndex) => {
                      return item.subSectionParts.map((subPart, partIndex) => {
                        console.log("asc2 subpart", subPart);
                        return (
                          <Draggable
                            key={`draggable-${subPart?.subsectionid}`}
                            draggableId={subPart?.subsectionid}
                            index={partIndex}
                          >
                            {(provided, snapshot) => {
                              return (
                                <SingleMaterial
                                  provided={provided}
                                  key={`${partIndex}-${subPart?.subsectionid}`}
                                  subSection={subPart}
                                  sectionid={sectionid}
                                  sectionIndex={index}
                                  subSectionIndex={subSectionIndex}
                                  subPartIndex={partIndex}
                                  sectionList={sectionList}
                                  setSectionList={setSectionList}
                                  totalHoursMain={totalHoursMain}
                                  setTotalHoursMain={setTotalHoursMain}
                                  totalAmountMain={totalAmountMain}
                                  setTotalAmountMain={setTotalAmountMain}
                                  vatTotal={vatTotal}
                                  setVatTotal={setVatTotal}
                                  roleAccess={roleAccess}
                                  quoteid={quoteid}
                                  mainMargin={margin}
                                  mainULaborCost={uLaborCost}
                                  productPartClickRef={productPartClickRef}
                                />
                              );
                            }}
                          </Draggable>
                        );
                      });
                    })}
                  </div>
                );
              }}
            </Droppable>
          </DragDropContext>
          <div
            className="single-sec-cont-head sec-content"
            style={{ cursor: "default" }}
          >
            <div style={{ borderTop: "1px solid white" }}></div>
            <div className="sec-material-div" style={{ position: "relative" }}>
              <p
                className="p-2 newDesign"
                style={{ cursor: "pointer", color: "#9a9999" }}
                onClick={() => setShowMaterialPopup(true)}
              >
                Type an item
              </p>
              <div
                ref={materialPopupRef}
                className="material-popup"
                style={{
                  transform: showMaterialPopup ? "scale(1)" : "scale(0)",
                  transitionDuration: "300ms",
                }}
              >
                <div className="m-popup-input-div">
                  <input
                    className="material-popup-input"
                    value={material}
                    onChange={(e) => setMaterial(e.target.value)}
                    // onFocus={true}
                    autoFocus
                    ref={materialInputRef}
                  />
                  <img
                    src={cross2}
                    alt=""
                    onClick={() => setShowMaterialPopup(false)}
                    style={{
                      placeSelf: "end",
                      marginRight: "10px",
                      cursor: "pointer",
                      alignSelf: "center",
                    }}
                  />
                </div>
                <div></div>
                <div class="material-items">
                  {productList?.map((product, index) => {
                    return (
                      <div
                        class="material-items-grid pointer"
                        key={index}
                        onClick={() =>
                          productPartClickRef.current[index].click()
                        }
                      >
                        <p
                          className="single-material-item-text"
                          style={{
                            marginTop: "24px",
                            textTransform: "capitalize",
                          }}
                        >
                          {product?.productPartName}
                        </p>

                        {packageId === process.env.REACT_APP_IS_PREMIUM ? (
                          <img
                            alt=""
                            className="sec-material-img"
                            style={{
                              marginBottom: "0",
                              border: product?.cover
                                ? "0.5px solid #7070704D"
                                : "none",
                              height: !product?.cover && "0",
                            }}
                            src={product?.cover}
                          />
                        ) : (
                          ""
                        )}

                        <div className="single-material-item-right">
                          <p style={{ marginTop: "24px" }}>Stock</p>
                          {!product?.supplier?.length === 0 ? (
                            <div className="sm-empty-stock">
                              <p>-</p>
                              <div
                                className="material-checkbox"
                                style={{
                                  backgroundColor: "transparent",
                                  boxShadow: "none",
                                }}
                              ></div>
                            </div>
                          ) : (
                            <div className="sm-item-check">
                              <div className="sm-item-check-2">
                                <div className="s-m-stock-div">
                                  <div className="sm-stock">
                                    <div
                                      className="sm-stock-color"
                                      style={{
                                        backgroundColor: `${product?.color}`,
                                      }}
                                    ></div>
                                    <p className="sm-stock-text">
                                      {product?.instock}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div
                                className="material-checkbox"
                                ref={(el) =>
                                  (productPartClickRef.current[index] = el)
                                }
                                style={{
                                  backgroundColor: selectedProducts?.find(
                                    (product2) =>
                                      product2.productPartid ===
                                      product?.productPartid,
                                  )
                                    ? "#004289"
                                    : "transparent",
                                  boxShadow: selectedProducts?.find(
                                    (product2) =>
                                      product2.productPartid ===
                                      product?.productPartid,
                                  )
                                    ? "0px 3px 6px #34394b29"
                                    : "none",
                                }}
                                onClick={() => {
                                  let list = [...selectedProducts];
                                  let found;
                                  list?.length > 0
                                    ? (found = list.findIndex(
                                        (prod) =>
                                          prod.productPartid ===
                                          productList[index].productPartid,
                                      ))
                                    : (found = false);
                                  if (found >= 0 && found !== false) {
                                    list.splice(found, 1);
                                    return setSelectedProducts([...list]);
                                  }
                                  let item = {
                                    productPartid:
                                      productList[index]?.productPartid,
                                    qty: 0,
                                    unitPrice: Number(
                                      // productList[index]?.salePriceexcvat
                                      productList[
                                        index
                                      ]?.salePriceexcvat?.replace(",", "."),
                                    ),
                                    cover: productList[index]?.cover,
                                    materialTotal: 0,
                                    hours: 0,
                                    hoursUnitPrice:
                                      uLaborCost?.length > 0 ? +uLaborCost : 0,
                                    hoursTotal: 0,
                                    margin: margin?.length > 0 ? +margin : 0,
                                    total: 0,
                                    indexPosition: subSections?.length,
                                  };
                                  list.push(item);
                                  setSelectedProducts([...list]);
                                }}
                              ></div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ textAlign: "center" }}>
                  <button
                    className="material-add-btn"
                    onClick={() => {
                      if (roleAccess === ROLES.VIEWER)
                        return handleGeneralNotification({
                          notificationType: "CUSTOM",
                          text: `You don't have permission to add/edit quotes.`,
                        });

                      let list = [...selectedProducts];
                      list.map((item, index) => {
                        item["indexPosition"] = subSections?.length + index;
                      });

                      const subSectionParts = selectedProducts.map(
                        (item, index) => ({
                          // subsectionPartid: item.subsectionPartid,
                          // subsectionid: sectionid,
                          productPartid: item.productPartid,
                          // productPartCode: item.productPartCode || "",
                          // productPartName: item.productPartName || "",
                          qty: item.qty || 0,
                          unitPrice: item.unitPrice || 0,
                          total: item.total || 0,
                          indexPosition: subSections?.length + index,
                          measureUnit: item.measureUnit || "",
                          suffix: item.suffix || "",
                        }),
                      );

                      const firstItem = selectedProducts[0] || {};

                      const payload = [
                        {
                          subSectionParts,
                          // qty: firstItem.qty || 0,
                          // unitPrice: firstItem.unitPrice || 0,
                          // metrialTotal: firstItem.metrialTotal || 0,
                          // hours: firstItem.hours || 0,
                          // hoursUnitPrice: firstItem.hoursUnitPrice || 0,
                          // hoursTotal: firstItem.hoursTotal || 0,
                          // margin: firstItem.margin || 0,
                          total: subSectionParts.reduce(
                            (acc, curr) => acc + (curr.total || 0),
                            0,
                          ),
                          // indexPosition: subSections?.length || 0,
                          // isProvisional: 0,
                          // isApproved: true,
                          // title: firstItem.title || "",
                          // description: firstItem.description || "",
                          // measureUnit: firstItem.measureUnit || "",
                          // totalizeAmountInSectionHeader: 0,
                        },
                      ];

                      dispatch(addSubSection(sectionid, payload));
                      setShowMaterialPopup(false);
                      setSelectedProducts([]);
                      setMaterial(""); // search clear.
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
            <p></p>
            <p></p>
            <p></p>
            <p></p>
            <p></p>
            <p></p>
            <p></p>
            <p></p>
            <p></p>
          </div>
        </div>
      </div>
    </div>
  );
};

const SingleMaterial = ({
  provided,
  key,
  subSection,
  sectionid,
  sectionIndex,
  subSectionIndex,
  subPartIndex,
  sectionList,
  setSectionList,
  totalHoursMain,
  setTotalHoursMain,
  totalAmountMain,
  setTotalAmountMain,
  vatTotal,
  setVatTotal,
  roleAccess,
  quoteid,
  mainMargin,
  mainULaborCost,
}) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const marginRef = useRef(null);
  const [showInput, setShowInput] = useState(false);
  const [quantity, setQuantity] = useState(subSection?.qty);
  const [hours, setHours] = useState(subSection?.hours);
  const [hoursUnitPrice, setHoursUnitPrice] = useState(
    subSection?.hoursUnitPrice,
  );
  const [hoursTotal, setHoursTotal] = useState(subSection?.hoursTotal);
  const [margin, setMargin] = useState(subSection?.margin);
  const [materialTotal, setMaterialTotal] = useState(subSection?.metrialTotal);
  const [total, setTotal] = useState(subSection?.total);

  console.log("asc22", sectionList);

  return (
    <div
      className="single-sec-cont-head sec-content"
      key={key}
      ref={provided.innerRef}
      {...provided.draggableProps}
    >
      <div className="drag-icon" {...provided.dragHandleProps}>
        <div className="drag-icon-cont">
          <div className="drag-line"></div>
          <div className="drag-line"></div>
        </div>
      </div>
      <div className="sec-material-flex" style={{ cursor: "default" }}>
        {subSection?.cover ? (
          <img className="sec-material-img" src={subSection?.cover} alt="" />
        ) : (
          <img
            className="sec-material-img"
            src={Package}
            alt=""
            style={{ objectFit: "fill" }}
          />
        )}

        <div className="sec-material-div">
          <p
            style={{
              whiteSpace: "pre-wrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={
              subSection?.productPartCode?.length > 7 &&
              subSection?.productPartCode
            }
          >
            {subSection?.productPartCode}
          </p>
          <p>{subSection?.productPartName}</p>
        </div>
      </div>
      <div style={{ width: "100%", overflow: "hidden" }}>
        <input
          value={quantity}
          min={0}
          name="qty"
          type="number"
          onClick={() => {
            if (quantity !== 0) return;
            setQuantity("");
          }}
          placeholder="0"
          onChange={(e) => {
            let list = [...sectionList];
            console.log("asc23", list);
            const { value, name } = e.target;
            const numericValue = Math.max(0, Number(value)); // we can't edit in minus value.

            // let object = list[sectionIndex]?.subSections[subSectionIndex];
            let object =
              list[sectionIndex]?.subSections[subSectionIndex]?.subSectionParts[
                subPartIndex
              ];
            // object[name] = Number(value);
            object[name] = numericValue;
            object["metrialTotal"] = numericValue * subSection?.unitPrice;
            let materialTotal = numericValue * subSection?.unitPrice;

            const currentHoursTotal = object["hoursTotal"] || 0;
            const currentMargin = object["margin"] || 0;

            object["total"] =
              materialTotal +
              currentHoursTotal +
              ((materialTotal + currentHoursTotal) * currentMargin) / 100;

            // list[sectionIndex]?.subSections?.splice(subSectionIndex, 1, object);
            list[sectionIndex].subSections[
              subSectionIndex
            ].subSectionParts.splice(subPartIndex, 1, object);

            setQuantity(numericValue);
            setMaterialTotal(materialTotal);
            setTotal(object["total"]);
            setSectionList([...list]);

            let totalAmount = 0;
            // list[sectionIndex]?.subSections?.map((subSection) => {
            //   totalAmount += +subSection?.total;
            // });

            list[sectionIndex]?.subSections?.map((subSection) => {
              subSection?.subSectionParts?.map((part) => {
                totalAmount += +part?.total || 0;
              });
            });

            list[sectionIndex]["totalAmount"] = totalAmount;

            let vatTotal2 = 0;
            list?.map((section) => (vatTotal2 += +section.totalAmount));
            setTotalAmountMain(totalAmount);
            setVatTotal(vatTotal2);
            setSectionList([...list]);
          }}
        />
      </div>
      {/* <p style={{ textAlign: "center", fontWeight: "bold" }}>{subSection?.qty}</p> */}
      <p>{europeanFormatWithSymbol(subSection?.unitPrice)}</p>
      <p
        style={{
          fontWeight: "bold",
          backgroundColor: "#CFD9E4",
          width: "100%",
          overflow: "hidden",
        }}
      >
        {europeanFormatWithSymbol(materialTotal)}
      </p>
      <div style={{ width: "100%", overflow: "hidden" }}>
        <input
          type="number"
          value={hours}
          name="hours"
          onClick={() => {
            if (hours !== 0) return;
            setHours("");
          }}
          placeholder="0"
          style={{ textAlign: "end" }}
          onChange={(e) => {
            let list = [...sectionList];
            const { value, name } = e.target;

            // let object = list[sectionIndex]?.subSections[subSectionIndex];
            let object =
              list[sectionIndex]?.subSections[subSectionIndex]?.subSectionParts[
                subPartIndex
              ];

            object[name] = Number(value);
            object["hoursTotal"] = Number(value) * hoursUnitPrice;
            let hoursTotal = Number(value) * hoursUnitPrice;
            object["total"] =
              materialTotal +
              hoursTotal +
              ((materialTotal + hoursTotal) * margin) / 100;

            // list[sectionIndex]?.subSections?.splice(subSectionIndex, 1, object);
            list[sectionIndex].subSections[
              subSectionIndex
            ].subSectionParts.splice(subPartIndex, 1, object);

            setHours(value);
            setHoursTotal(Number(value) * hoursUnitPrice);
            setSectionList([...list]);
            setTotal(object["total"]);

            let totalHours = 0;
            let totalAmount = 0;

            list[sectionIndex]?.subSections?.map((subSection) => {
              subSection?.subSectionParts?.map((part) => {
                totalHours += +part?.hours || 0;
                totalAmount += +part?.total || 0;
              });
            });

            list[sectionIndex]["totalHours"] = totalHours;
            list[sectionIndex]["totalAmount"] = totalAmount;

            setTotalAmountMain(totalAmount);
            setTotalHoursMain(totalHours);
            let vatTotal2 = 0;
            list?.map((section) => (vatTotal2 += +section.totalAmount));
            setVatTotal(vatTotal2);
            setSectionList([...list]);
          }}
        />
      </div>
      <div style={{ width: "100%", textAlign: "end", overflow: "hidden" }}>
        <input
          ref={inputRef}
          value={hoursUnitPrice}
          style={{
            textAlign: "end",
            fontWeight: "700",
            display: showInput ? "block" : "none",
          }}
          type="number"
          onClick={() => {
            if (hoursUnitPrice !== 0) return;
            setHoursUnitPrice("");
          }}
          placeholder="€ 0,00"
          name="hoursUnitPrice"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              inputRef.current.blur();
              setShowInput(false);
            }
          }}
          onBlur={() => {
            setShowInput(false);
          }}
          onChange={(e) => {
            let list = [...sectionList];
            const { value, name } = e.target;

            // let object = list[sectionIndex]?.subSections[subSectionIndex];
            let object =
              list[sectionIndex]?.subSections[subSectionIndex]?.subSectionParts[
                subPartIndex
              ];

            object[name] = Number(value);
            object["hoursTotal"] = Number(value) * hours;
            let hoursTotal = Number(value) * hours;
            object["total"] =
              materialTotal +
              hoursTotal +
              ((materialTotal + hoursTotal) * margin) / 100;

            // list[sectionIndex]?.subSections?.splice(subSectionIndex, 1, object);
            list[sectionIndex].subSections[
              subSectionIndex
            ].subSectionParts.splice(subPartIndex, 1, object);

            setHoursUnitPrice(value);
            setHoursTotal(Number(value) * hours);
            setTotal(object["total"]);
            setSectionList([...list]);

            let totalAmount = 0;
            // list[sectionIndex]?.subSections?.map(
            //   (subSection) => (totalAmount += +subSection?.total)
            // );
            list[sectionIndex]?.subSections?.map((subSection) => {
              subSection?.subSectionParts?.map((part) => {
                totalAmount += +part?.total || 0;
              });
            });

            list[sectionIndex]["totalAmount"] = totalAmount;

            setTotalAmountMain(totalAmount);
            let vatTotal2 = 0;
            list?.map((section) => (vatTotal2 += +section.totalAmount));
            setVatTotal(vatTotal2);
            setSectionList([...list]);
          }}
        />
        <p
          onClick={() => {
            if (
              (mainULaborCost?.length > 0 &&
                (ROLES.MANAGER === roleAccess || ROLES.OWNER === roleAccess)) ||
              mainULaborCost?.length === 0
            ) {
              inputRef.current.focus();
              setShowInput(true);
              return;
            } else {
              return;
            }
          }}
          style={{
            display: showInput ? "none" : "block",
            paddingRight: "5px",
            fontWeight: "700",
            marginTop: "6px",
            overflow: "hidden",
          }}
        >
          {europeanFormatWithSymbol(hoursUnitPrice)}
        </p>
      </div>
      {/* <p style={{ fontWeight: "bold" }}>{subSection?.hours}</p> */}
      {/* <p>{europeanFormatWithSymbol(subSection?.hoursUnitPrice)}</p> */}
      <p
        style={{
          fontWeight: "bold",
          backgroundColor: "#CFD9E4",
          width: "100%",
          overflow: "hidden",
        }}
      >
        {europeanFormatWithSymbol(hoursTotal)}
      </p>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <input
          ref={marginRef}
          value={margin}
          name="margin"
          onClick={() => {
            if (margin !== 0) return;
            setMargin("");
          }}
          placeholder="0"
          type="number"
          disabled={
            (mainMargin?.length > 0 &&
              (ROLES.MANAGER === roleAccess || ROLES.OWNER === roleAccess)) ||
            mainMargin?.length === 0
              ? false
              : true
          }
          onChange={(e) => {
            let list = [...sectionList];
            const { value, name } = e.target;

            // let object = list[sectionIndex]?.subSections[subSectionIndex];
            let object =
              list[sectionIndex]?.subSections[subSectionIndex]?.subSectionParts[
                subPartIndex
              ];

            object[name] = Number(value);
            setMargin(value);
            let total =
              materialTotal +
              hoursTotal +
              ((materialTotal + hoursTotal) * value) / 100;
            object["total"] = total;
            setTotal(total);

            // list[sectionIndex]?.subSections?.splice(subSectionIndex, 1, object);
            list[sectionIndex].subSections[
              subSectionIndex
            ].subSectionParts.splice(subPartIndex, 1, object);

            setSectionList([...list]);

            let totalAmount = 0;
            // list[sectionIndex]?.subSections?.map(
            //   (subSection) => (totalAmount += +subSection?.total)
            // );
            list[sectionIndex]?.subSections?.map((subSection) => {
              subSection?.subSectionParts?.map((part) => {
                totalAmount += +part?.total || 0;
              });
            });

            list[sectionIndex]["totalAmount"] = totalAmount;

            setTotalAmountMain(totalAmount);
            let vatTotal2 = 0;
            list?.map((section) => (vatTotal2 += +section.totalAmount));
            setVatTotal(vatTotal2);
            setSectionList([...list]);
          }}
          className="margin-input"
          style={{
            width: margin?.length ? `${margin?.length * 10}px` : "30px",
          }}
        />
        <span style={{ marginTop: "6px" }}>%</span>
      </div>
      <p
        style={{
          fontWeight: "bold",
          backgroundColor: "#CFD9E4",
          overflow: "hidden",
        }}
      >
        {europeanFormatWithSymbol(total)}
      </p>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "15px",
        }}
      >
        <input
          class="form-check-input pointer ms-3"
          style={{
            borderRadius: "50%",
            border: "1px solid #9A9999",
            width: "20px",
            height: "20px",
          }}
          type="checkbox"
          // value="Sent"
          id="ascInvoice"
          checked={subSection.isProvisional}
          onClick={(e) => {
            let list = [...sectionList];

            // let object = list[sectionIndex]?.subSections[subSectionIndex];
            let object =
              list[sectionIndex]?.subSections[subSectionIndex]?.subSectionParts[
                subPartIndex
              ];

            object["isProvisional"] = e.target.checked ? 1 : 0;
            setSectionList([...list]);
          }}
        />

        <img
          src={Bin}
          alt=""
          style={{
            cursor: "pointer",
            textAlign: "right",
            paddingTop: "5px",
          }}
          onClick={() => {
            if (roleAccess === ROLES.VIEWER)
              return handleGeneralNotification({
                notificationType: "CUSTOM",
                text: `You don't have permission to add/edit quotes.`,
              });
            dispatch(
              // deleteSubSection(quoteid, sectionid, subSection?.subsectionid)
              DeleteSubSectionParts(subSection?.subsectionPartid),
            );
          }}
        />
      </div>
    </div>
  );
};
